import type React from "react";
import { useCallback, useMemo, useState } from "react";
import { Button, Flex, RadioButton, RadioGroup, Text } from "@jsq/ds/core";
import { useFormContext } from "@jsq/ds/forms";
import { Sizes } from "@jsq/ds/styles";
import { FIELD_RENDERERS } from "../DynamicField/fieldRenderers";
import * as styles from "./styles_vanilla.css";
import type { FormFieldConfig, TaxFormValues, TaxResidencyEntry } from "../../types";

type TaxResidencyFormProps = {
  fieldName: string;
  index: number;
  onSubmit: () => void;
  onCancel: () => void;
  isNewEntry: boolean;
};

export const TaxResidencyForm = ({
  fieldName,
  index,
  onSubmit,
  onCancel,
  isNewEntry,
}: TaxResidencyFormProps) => {
  const { values, setFieldValue, setFieldTouched } = useFormContext<TaxFormValues>();

  const entries = (values[fieldName] as TaxResidencyEntry[]) || [];
  const currentEntry = entries[index];

  const [hasTaxRef, setHasTaxRef] = useState<boolean>(
    currentEntry?.hasTaxReferenceNumber ?? true,
  );

  const basePath = `${fieldName}.${index}`;

  const fieldConfigs = useMemo((): Record<string, FormFieldConfig> => {
    return {
      jurisdiction: {
        name: `${basePath}.jurisdiction`,
        type: "country",
        label: "Jurisdiction of tax residency",
        placeholder: "Select a country",
        required: false,
        gridColumn: "full",
      },
      taxReferenceNumberType: {
        name: `${basePath}.taxReferenceNumberType`,
        type: "text",
        label: "Tax reference number type",
        placeholder: "",
        required: false,
      },
      taxReferenceNumber: {
        name: `${basePath}.taxReferenceNumber`,
        type: "text",
        label: "Tax reference number",
        placeholder: "",
        required: false,
      },
      reasonForNoTin: {
        name: `${basePath}.reasonForNoTin`,
        type: "textarea",
        label: "If no TIN, specify reason",
        placeholder: "Specify reason for no TIN",
        required: false,
      },
    };
  }, [basePath]);

  const handleRadioChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newHasTaxRef = e.target.value === "yes";
      setHasTaxRef(newHasTaxRef);
      void setFieldValue(`${basePath}.hasTaxReferenceNumber`, newHasTaxRef);
      if (newHasTaxRef) {
        void setFieldValue(`${basePath}.reasonForNoTin`, "");
      } else {
        void setFieldValue(`${basePath}.taxReferenceNumberType`, "");
        void setFieldValue(`${basePath}.taxReferenceNumber`, "");
      }
    },
    [basePath, setFieldValue],
  );

  const handleSubmit = useCallback(() => {
    // Touch fields to show Yup error messages in UI
    void setFieldTouched(`${basePath}.jurisdiction`, true, true);
    if (hasTaxRef) {
      void setFieldTouched(`${basePath}.taxReferenceNumberType`, true, true);
      void setFieldTouched(`${basePath}.taxReferenceNumber`, true, true);
    } else {
      void setFieldTouched(`${basePath}.reasonForNoTin`, true, true);
    }

    // Check values synchronously (setFieldTouched is async, errors state is stale)
    const jurisdiction = currentEntry?.jurisdiction;
    if (!jurisdiction) {
      return;
    }

    if (hasTaxRef) {
      if (!currentEntry?.taxReferenceNumberType || !currentEntry?.taxReferenceNumber) {
        return;
      }
    } else {
      if (!currentEntry?.reasonForNoTin) {
        return;
      }
    }

    onSubmit();
  }, [basePath, currentEntry, hasTaxRef, onSubmit, setFieldTouched]);

  return (
    <div className={styles.formContainer}>
      <Flex flexDirection="column" gap={Sizes.MD}>
        {FIELD_RENDERERS.country?.(fieldConfigs.jurisdiction, "Jurisdiction of tax residency")}
        <RadioGroup
          name={`${basePath}.hasTaxReferenceNumber`}
          value={hasTaxRef ? "yes" : "no"}
          onChange={handleRadioChange}
          data-testid="field-hasTaxReferenceNumber"
        >
          <Flex flexDirection="column" gap={Sizes.SM}>
            <RadioButton value="yes">
              <Text>I have a tax reference number</Text>
            </RadioButton>
            <RadioButton value="no">
              <Text>I don&apos;t have a tax reference number</Text>
            </RadioButton>
          </Flex>
        </RadioGroup>
        {hasTaxRef && (
          <Flex gap={Sizes.MD} className={styles.formFieldRow}>
            <div className={styles.formFieldHalf}>
              {FIELD_RENDERERS.text?.(
                fieldConfigs.taxReferenceNumberType,
                "Tax reference number type",
              )}
            </div>
            <div className={styles.formFieldHalf}>
              {FIELD_RENDERERS.text?.(fieldConfigs.taxReferenceNumber, "Tax reference number")}
            </div>
          </Flex>
        )}
        {!hasTaxRef &&
          FIELD_RENDERERS.textarea?.(fieldConfigs.reasonForNoTin, "If no TIN, specify reason")}
      </Flex>
      <Flex gap={Sizes.SM} className={styles.formButtonsContainer}>
        <Button onClick={handleSubmit} data-testid="add-tax-residency-submit">
          {isNewEntry ? "Add tax residency" : "Update tax residency"}
        </Button>
        <Button variant="neutral" onClick={onCancel} data-testid="add-tax-residency-cancel">
          Cancel
        </Button>
      </Flex>
    </div>
  );
};
